<template>
  <div class="sr-page">
    <breadcrumb :items="breadcrumbs"></breadcrumb>

    <div class="sr-content">
      <!-- Header -->
      <div class="sr-header">
        <div class="sr-header-left">
          <div class="sr-title">{{ assessmentTitle || $t('surveyBuilder.surveyResponsesTitle') }}</div>
          <div v-if="assessmentDescription" class="sr-desc">{{ assessmentDescription }}</div>
          <div v-if="showView !== 'responseDetail'" class="sr-count">
            {{ $t('esgPlatform.responses') }}: <strong>{{ totalSubmissions }}</strong>
          </div>
        </div>
        <div class="sr-switcher" v-if="showView !== 'responseDetail'">
          <button class="sr-btn" :class="{ active: isListView }" @click="switchView('responseList')">
            {{ $t('esgPlatform.responses') }}
          </button>
          <button class="sr-btn" :class="{ active: showView === 'responseSummary' }" @click="switchView('responseSummary')">
            {{ $t('surveyBuilder.allResponsesSummary') }}
          </button>
          <button class="sr-btn" :class="{ active: showView === 'responseByQuestions' }" @click="switchView('responseByQuestions')">
            {{ $t('surveyBuilder.responsesByQuestion') }}
          </button>
        </div>
        <div class="sr-back" v-else @click="switchView('responseList')">
          <v-icon size="18" color="#117443">mdi-arrow-left</v-icon>{{ $t('esgPlatform.esgReport.back') || 'BACK' }}
        </div>
      </div>

      <!-- ============ RESPONSES LIST ============ -->
      <div v-show="showView === 'responseList'" class="sr-card">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          :placeholder="$t('search')"
          outlined dense clearable
          style="max-width: 360px"
          class="mb-3"
          @input="debouncedSearch"
        ></v-text-field>

        <v-data-table
          :headers="visibleListHeaders"
          :items="submissions"
          :loading="loading"
          :server-items-length="totalSubmissions"
          :options.sync="tableOptions"
          :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
          class="sr-table elevation-0"
        >
          <template v-slot:item.farmName="{ item }">
            {{ item.farmCount ? item.farmCount : '—' }}
          </template>
          <template v-slot:item.responseDate="{ item }">
            {{ formatDate(item.responseDate) }}
          </template>
          <template v-slot:item.question="{ item }">
            <span class="sr-progress-text">{{ item.answeredQuestions }}/{{ item.totalQuestions }}</span>
          </template>
          <template v-slot:item.viewSurvey="{ item }">
            <button class="sr-icon-btn" @click="openDetail(item)">
              <v-icon size="20">mdi-magnify</v-icon>
            </button>
          </template>
          <template v-slot:no-data>
            <div class="py-6 sr-muted">{{ $t('surveyBuilder.noSubmissions') }}</div>
          </template>
        </v-data-table>
      </div>

      <!-- ============ SINGLE SUBMISSION DETAIL ============ -->
      <div v-if="showView === 'responseDetail'">
        <v-progress-linear v-if="detailLoading" indeterminate color="#0EBF67"></v-progress-linear>

        <template v-else-if="detail">
          <!-- Summary card -->
          <div class="sr-summary">
            <div class="sr-summary-cell">
              <div class="sr-summary-label">{{ $t('esgPlatform.userName') }}</div>
              <div class="sr-summary-value">{{ detail.submittedBy.name || '—' }}</div>
            </div>
            <div v-if="isFarmerAudience" class="sr-summary-cell">
              <div class="sr-summary-label">{{ $t('surveyBuilder.farmColumn') }}</div>
              <div class="sr-summary-value">{{ detail.submittedBy.farmCount ? detail.submittedBy.farmCount : '—' }}</div>
            </div>
            <div class="sr-summary-cell">
              <div class="sr-summary-label">{{ $t('surveyBuilder.responseDate') }}</div>
              <div class="sr-summary-value">{{ formatDate(detail.submittedBy.responseDate) }}</div>
            </div>
            <div class="sr-summary-cell">
              <div class="sr-summary-label">{{ $t('esgPlatform.question') }}</div>
              <div class="sr-summary-value">
                {{ detail.submittedBy.answeredQuestions }}/{{ detail.submittedBy.totalQuestions }}
              </div>
            </div>
          </div>

          <!-- Questions -->
          <div v-for="(section, si) in detailSections" :key="si" class="sr-section">
            <div v-if="section.title" class="sr-section-title">{{ section.title }}</div>
            <div
              v-for="entry in section.questions"
              :key="entry.q._id"
              class="sr-qcard"
            >
              <div class="sr-qhead">
                <div class="sr-qnum">{{ entry.number }}</div>
                <span class="sr-badge">{{ formatQuestionType(entry.q.questionType) }}</span>
                <span style="flex: 1"></span>
                <button class="sr-chevron" @click="toggleQuestion(entry.q._id)">
                  <v-icon size="22" color="#787878">
                    {{ isQuestionOpen(entry.q._id) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                </button>
              </div>
              <div v-if="isQuestionOpen(entry.q._id)" class="sr-qbody">
                <div class="sr-qtitle-body">{{ entry.q.title }}</div>
                <question-answer :question="entry.q"></question-answer>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- ============ ALL RESPONSES SUMMARY ============ -->
      <div v-if="showView === 'responseSummary'" class="sr-card">
        <v-progress-linear v-if="summaryLoading" indeterminate color="#0EBF67"></v-progress-linear>
        <template v-else>
          <div v-if="!summary.length" class="py-6 sr-muted">{{ $t('surveyBuilder.noSubmissions') }}</div>
          <div v-else class="sr-summary-list">
            <div v-for="(q, idx) in summary" :key="q._id" class="sr-qcard">
              <div class="sr-qhead">
                <div class="sr-qnum">{{ idx + 1 }}</div>
                <div class="sr-qtitle">{{ q.title }}</div>
                <span class="sr-badge">{{ formatQuestionType(q.questionType) }}</span>
              </div>
              <div v-if="q.description" class="sr-qdesc sr-summary-desc">{{ q.description }}</div>
              <div class="sr-qbody">
                <!-- radio / checkbox -> chart -->
                <div v-if="isOptionType(q.questionType)">
                  <apexchart
                    v-if="q.totalResponses > 0"
                    type="bar"
                    height="280"
                    :options="chartOptionsFor(q)"
                    :series="chartSeriesFor(q)"
                  ></apexchart>
                  <div v-else class="sr-muted">{{ $t('surveyBuilder.noSubmissions') }}</div>
                </div>
                <!-- other types -> per-user response counts + metadata -->
                <div v-else>
                  <!-- Get Data: data types collected -->
                  <div v-if="q.questionType === 'get-data' && q.getDataFields && q.getDataFields.length" class="sr-meta-row">
                    <span class="sr-meta-label">{{ $t('esgPlatform.getData') || 'Get Data' }}:</span>
                    <span v-for="(f, fi) in q.getDataFields" :key="fi" class="sr-chip">
                      {{ formatDataType(f.dataType) }}
                    </span>
                  </div>

                  <!-- Repeating question: scheduled dates -->
                  <div v-if="q.isRepeatEnabled && q.repetitionDates && q.repetitionDates.length" class="sr-meta-row">
                    <span class="sr-meta-label">{{ $t('surveyBuilder.repeatSubmissions') || 'Repeat submissions' }}:</span>
                    <span v-for="(d, di) in q.repetitionDates" :key="di" class="sr-chip">{{ formatDate(d) }}</span>
                  </div>

                  <!-- Per-user response counts -->
                  <table v-if="userResponseCounts(q).length" class="sr-user-table">
                    <thead>
                      <tr>
                        <th>{{ nameColumnLabel }}</th>
                        <th style="width: 160px" class="sr-user-count-col">{{ $t('esgPlatform.responses') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(u, ui) in userResponseCounts(q)" :key="ui">
                        <td>{{ u.name }}</td>
                        <td class="sr-user-count-col">{{ u.count }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div v-else class="sr-muted">{{ $t('surveyBuilder.noSubmissions') }}</div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="summaryTotal > pageSize" class="sr-pagination">
            <v-pagination
              :value="summaryPage"
              :length="summaryPageCount"
              :total-visible="7"
              color="#00bd73"
              @input="onSummaryPage"
            ></v-pagination>
          </div>
        </template>
      </div>

      <!-- ============ RESPONSES BY QUESTION ============ -->
      <div v-if="showView === 'responseByQuestions'" class="sr-card">
        <v-progress-linear v-if="byQuestionLoading" indeterminate color="#0EBF67"></v-progress-linear>
        <template v-else>
          <v-select
            v-model="selectedQuestionId"
            :items="byQuestion"
            item-text="title"
            item-value="_id"
            outlined dense
            :placeholder="$t('surveyBuilder.selectQuestion')"
            style="max-width: 640px"
          ></v-select>

          <template v-if="selectedQuestion">
            <div class="sr-byq-meta">
              <span class="sr-badge">{{ formatQuestionType(selectedQuestion.questionType) }}</span>
              <span class="sr-muted">{{ $t('esgPlatform.responses') }}: <strong>{{ byqTotal }}</strong></span>
            </div>

            <!-- Repeating question: clickable date filter tabs (All + each scheduled date) -->
            <div v-if="byqHasRepetition" class="sr-dates">
              <span class="sr-dates-label">{{ $t('surveyBuilder.repeatSubmissions') || 'Repeat submissions' }}:</span>
              <button
                v-for="(d, i) in byqDateTabs"
                :key="i"
                class="sr-date"
                :class="{ active: d.key === byqSelectedDateKey }"
                @click="selectByqDate(d.key)"
              >
                {{ d.label }}
              </button>
            </div>

            <div v-if="isFarmerAudience" class="sr-qdesc">
              {{ selectedQuestion.isSeparateFarm
                ? ($t('surveyBuilder.answeredByFarmsIndividually') || 'This question is answered by farms individually.')
                : ($t('surveyBuilder.singleResponsePerFarmer') || 'This question has a single response for each farmer.') }}
            </div>

            <v-progress-linear v-if="byqResponsesLoading" indeterminate color="#0EBF67"></v-progress-linear>

            <template v-else>
              <!-- radio / checkbox -> aggregate counts + flat paginated respondent table -->
              <div v-if="byqIsOption">
                <div v-if="byqOptionCounts && byqOptionCounts.length" class="sr-optcount-strip">
                  <span v-for="oc in byqOptionCounts" :key="oc.optionId" class="sr-optcount">
                    <span class="sr-optcount-label">{{ oc.label }}</span>
                    <span class="sr-optcount-num">{{ oc.count }}</span>
                  </span>
                </div>
                <table v-if="byqResponses.length" class="sr-user-table mt-3">
                  <thead>
                    <tr>
                      <th>{{ nameColumnLabel }}</th>
                      <th v-if="isFarmerAudience">{{ $t('surveyBuilder.farmColumn') }}</th>
                      <th>{{ $t('esgPlatform.responses') }}</th>
                      <th style="width: 200px">{{ $t('surveyBuilder.submittedDate') || 'Submitted Date' }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(r, i) in byqResponses" :key="i">
                      <td>{{ r.submittedByName || '—' }}</td>
                      <td v-if="isFarmerAudience">{{ r.farmName || '—' }}</td>
                      <td>{{ optionLabelsFor(r.selectedOptionIds) }}</td>
                      <td>{{ formatDate(r.submittedOn) }}</td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="sr-muted py-4">{{ $t('surveyBuilder.noSubmissions') }}</div>
              </div>

              <!-- other types -> respondent breakdown (farm-wise when separate) -->
              <div v-else>
                <respondent-breakdown
                  v-if="byqResponses.length"
                  :entries="byqResponses"
                  :is-separate-farm="selectedQuestion.isSeparateFarm"
                  :question-type="selectedQuestion.questionType"
                  :options="selectedQuestion.options"
                  :name-column-label="nameColumnLabel"
                />
                <div v-else class="sr-muted py-4">{{ $t('surveyBuilder.noSubmissions') }}</div>
              </div>

              <div v-if="byqTotal > pageSize" class="sr-pagination">
                <v-pagination
                  :value="byqPage"
                  :length="byqPageCount"
                  :total-visible="7"
                  color="#00bd73"
                  @input="onByqPage"
                ></v-pagination>
              </div>
            </template>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import moment from "moment";
import SurveyService from "@/_services/SurveyService";
import QuestionAnswer from "./components/QuestionAnswer.vue";
import RespondentBreakdown from "./components/RespondentBreakdown.vue";

const CHART_COLORS = [
  "#0EBF67", "#117443", "#00BD73", "#4BBF9F", "#1F5D51",
  "#0170E5", "#F0A537", "#9C27B0", "#F03737", "#607D8B",
];

export default {
  name: "SurveyResponses",
  components: {
    "question-answer": QuestionAnswer,
    "respondent-breakdown": RespondentBreakdown,
  },
  data() {
    return {
      showView: "responseList",
      submissions: [],
      totalSubmissions: 0,
      search: "",
      loading: false,
      tableOptions: { page: 1, itemsPerPage: 10 },
      assessmentTitle: "",
      assessmentDescription: "",
      assessmentRespondentType: null,
      detail: null,
      detailLoading: false,
      expandedQuestions: {},
      summary: [],
      summaryLoading: false,
      summaryPage: 1,
      summaryTotal: 0,
      pageSize: 10,
      byQuestion: [],
      byQuestionLoading: false,
      selectedQuestionId: null,
      byqSelectedDateKey: null,
      byqResponses: [],
      byqResponsesLoading: false,
      byqOptionCounts: null,
      byqPage: 1,
      byqTotal: 0,
    };
  },
  computed: {
    surveyId() {
      return this.$route.params.id;
    },
    // Sub-Organization is not a farmer audience — Farm / Farmer Name concepts
    // don't apply and must not be shown. Farmer-typed surveys (Connected
    // Farmer/Cacao/Coffee, Farmer Single/Separate) do.
    isFarmerAudience() {
      return !!this.assessmentRespondentType && this.assessmentRespondentType !== "Sub-Organization";
    },
    nameColumnLabel() {
      return this.isFarmerAudience ? this.$t("surveyBuilder.farmerNameColumn") : this.$t("esgPlatform.userName");
    },
    listHeaders() {
      return [
        { text: this.$t("esgPlatform.userName"), value: "userName", sortable: false },
        { text: this.$t("surveyBuilder.farmColumn"), value: "farmName", sortable: false },
        { text: this.$t("surveyBuilder.farmerNameColumn"), value: "farmerName", sortable: false },
        { text: this.$t("surveyBuilder.responseDate"), value: "responseDate", sortable: false },
        { text: this.$t("esgPlatform.question"), value: "question", sortable: false, align: "center" },
        { text: this.$t("surveyBuilder.viewSurvey"), value: "viewSurvey", sortable: false, align: "center" },
      ];
    },
    visibleListHeaders() {
      return this.isFarmerAudience
        ? this.listHeaders
        : this.listHeaders.filter((h) => h.value !== "farmName" && h.value !== "farmerName");
    },
    isListView() {
      return this.showView === "responseList" || this.showView === "responseDetail";
    },
    breadcrumbs() {
      return [
        { text: this.$t("dashboard"), disabled: false, to: { name: "Dashboard" } },
        { text: this.$t("surveyBuilder.surveyBuilder"), disabled: false, to: { name: "SurveyList" } },
        {
          text: this.$t("esgPlatform.surveyDetails") || "Survey Detail",
          disabled: false,
          to: { name: "SurveyDetails", params: { id: this.surveyId } },
        },
        { text: this.$t("esgPlatform.responses"), disabled: true },
      ];
    },
    selectedQuestion() {
      return this.byQuestion.find((q) => q._id === this.selectedQuestionId) || null;
    },
    // Repeating-question date tabs for the "Responses By Question" view.
    // Server-side date filtering: an "All" tab (null key) plus one per scheduled date.
    byqHasRepetition() {
      const q = this.selectedQuestion;
      return !!(q && q.isRepeatEnabled && (q.repetitionDates || []).length);
    },
    byqDateTabs() {
      const q = this.selectedQuestion;
      if (!q) return [];
      const tabs = [{ key: null, label: this.$t("esgPlatform.all") || "All" }];
      (q.repetitionDates || []).forEach((raw) => {
        tabs.push({ key: moment(raw).format("YYYY-MM-DD"), label: raw ? moment(raw).format("DD/MM/YY") : "—" });
      });
      return tabs;
    },
    byqIsOption() {
      const q = this.selectedQuestion;
      return !!q && this.isOptionType(q.questionType);
    },
    byqPageCount() {
      return Math.max(Math.ceil(this.byqTotal / this.pageSize), 1);
    },
    summaryPageCount() {
      return Math.max(Math.ceil(this.summaryTotal / this.pageSize), 1);
    },
    // Flatten headings into sections with sequential question numbers.
    detailSections() {
      if (!this.detail) return [];
      let n = 0;
      return (this.detail.assessmentQuestion || []).map((h) => ({
        title: h.title,
        questions: (h.assessmentQuestions || []).map((q) => {
          n += 1;
          return { q, number: n };
        }),
      }));
    },
  },
  watch: {
    selectedQuestionId() {
      // New question selected -> reset date filter + paging, load its first page.
      this.byqSelectedDateKey = null;
      this.byqPage = 1;
      this.byqResponses = [];
      this.byqOptionCounts = null;
      this.byqTotal = 0;
      if (this.selectedQuestionId) this.loadByQuestionResponses();
    },
    "tableOptions.page"() {
      if (this.showView === "responseList") this.loadSubmissions();
    },
    "tableOptions.itemsPerPage"() {
      if (this.showView === "responseList") this.loadSubmissions();
    },
  },
  mounted() {
    this.loadSubmissions();
  },
  methods: {
    switchView(view) {
      this.showView = view;
      if (view === "responseList") this.loadSubmissions();
      if (view === "responseSummary" && !this.summary.length) this.loadSummary();
      if (view === "responseByQuestions" && !this.byQuestion.length) this.loadByQuestionList();
    },
    toggleQuestion(id) {
      this.$set(this.expandedQuestions, id, !this.isQuestionOpen(id));
    },
    isQuestionOpen(id) {
      return this.expandedQuestions[id] !== undefined ? this.expandedQuestions[id] : true;
    },
    debouncedSearch: debounce(function () {
      this.tableOptions.page = 1;
      this.loadSubmissions();
    }, 700),
    async loadSubmissions() {
      this.loading = true;
      try {
        const params = {
          page: this.tableOptions.page,
          limit: this.tableOptions.itemsPerPage,
          search: this.search || undefined,
        };
        const res = await SurveyService.getSurveySubmissions(this.surveyId, params);
        const data = res.data || {};
        this.submissions = data.info || [];
        this.totalSubmissions = data.total || 0;
        if (data.assessment) {
          this.assessmentTitle = data.assessment.title || "";
          this.assessmentDescription = this.stripHtml(data.assessment.description || "");
          this.assessmentRespondentType = data.assessment.respondentType || null;
        }
      } catch (err) {
        this.notifyError(err);
        this.submissions = [];
        this.totalSubmissions = 0;
      }
      this.loading = false;
    },
    async openDetail(item) {
      this.showView = "responseDetail";
      this.detail = null;
      this.detailLoading = true;
      try {
        const res = await SurveyService.getSurveySubmissionDetail(this.surveyId, item.submissionId);
        this.detail = res.data || null;
        if (this.detail && this.detail.assessmentdata) {
          this.assessmentRespondentType = this.detail.assessmentdata.respondentType || this.assessmentRespondentType;
        }
      } catch (err) {
        this.notifyError(err);
      }
      this.detailLoading = false;
    },
    async loadSummary(page = 1) {
      this.summaryPage = page;
      this.summaryLoading = true;
      try {
        const res = await SurveyService.getSurveyResponsesSummary(this.surveyId, {
          page: this.summaryPage,
          limit: this.pageSize,
        });
        const data = res.data || {};
        this.summary = (data.summary || []).map((q) => ({
          ...q,
          description: this.stripHtml(q.description || ""),
        }));
        this.summaryTotal = data.total || this.summary.length;
        if (data.assessment) {
          this.assessmentRespondentType = data.assessment.respondentType || this.assessmentRespondentType;
        }
      } catch (err) {
        this.notifyError(err);
        this.summary = [];
        this.summaryTotal = 0;
      }
      this.summaryLoading = false;
    },
    onSummaryPage(page) {
      this.loadSummary(page);
    },
    async loadByQuestionList() {
      this.byQuestionLoading = true;
      try {
        const res = await SurveyService.getSurveyResponsesByQuestion(this.surveyId, {});
        this.byQuestion = (res.data && res.data.questions) || [];
        if (res.data && res.data.assessment) {
          this.assessmentRespondentType = res.data.assessment.respondentType || this.assessmentRespondentType;
        }
        if (this.byQuestion.length && !this.selectedQuestionId) {
          // Triggers the selectedQuestionId watcher, which loads the first page.
          this.selectedQuestionId = this.byQuestion[0]._id;
        }
      } catch (err) {
        this.notifyError(err);
        this.byQuestion = [];
      }
      this.byQuestionLoading = false;
    },
    async loadByQuestionResponses() {
      if (!this.selectedQuestionId) return;
      this.byqResponsesLoading = true;
      try {
        const res = await SurveyService.getSurveyResponsesByQuestion(this.surveyId, {
          questionId: this.selectedQuestionId,
          page: this.byqPage,
          limit: this.pageSize,
          date: this.byqSelectedDateKey || undefined,
        });
        const q = (res.data && res.data.question) || {};
        this.byqResponses = q.responses || [];
        this.byqOptionCounts = q.optionCounts || null;
        this.byqTotal = (res.data && res.data.total) || 0;
      } catch (err) {
        this.notifyError(err);
        this.byqResponses = [];
        this.byqOptionCounts = null;
        this.byqTotal = 0;
      }
      this.byqResponsesLoading = false;
    },
    onByqPage(page) {
      this.byqPage = page;
      this.loadByQuestionResponses();
    },
    selectByqDate(key) {
      this.byqSelectedDateKey = key;
      this.byqPage = 1;
      this.loadByQuestionResponses();
    },
    optionLabelsFor(selectedOptionIds) {
      const opts = (this.selectedQuestion && this.selectedQuestion.options) || [];
      return (selectedOptionIds || [])
        .map((id) => {
          const o = opts.find((x) => String(x._id) === String(id));
          return o ? o.label : null;
        })
        .filter(Boolean)
        .join(", ") || "—";
    },
    chartSeriesFor(q) {
      return [{ name: this.$t("esgPlatform.responses"), data: (q.optionCounts || []).map((o) => o.count) }];
    },
    chartOptionsFor(q) {
      const labels = (q.optionCounts || []).map((o) => o.label);
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit" },
        plotOptions: { bar: { distributed: true, horizontal: false, columnWidth: "45%", borderRadius: 4 } },
        colors: CHART_COLORS,
        dataLabels: { enabled: true },
        legend: { show: false },
        xaxis: { categories: labels },
        yaxis: { labels: { formatter: (v) => Math.round(v) } },
        grid: { borderColor: "#E8E8E8" },
      };
    },
    isOptionType(type) {
      return type === "radio-button" || type === "check-boxes";
    },
    // Group a question's responses by respondent -> number of responses each submitted.
    userResponseCounts(q) {
      const map = {};
      const order = [];
      for (const v of q.values || []) {
        const key = String(v.submittedById || v.submittedByName || "unknown");
        if (!map[key]) {
          map[key] = { name: v.submittedByName || "—", count: 0 };
          order.push(key);
        }
        map[key].count += 1;
      }
      return order.map((k) => map[k]);
    },
    formatDataType(type) {
      const map = {
        "text-field": this.$t("esgPlatform.textField") || "Text Field",
        "text-area": this.$t("esgPlatform.textArea") || "Text Area",
        numeric: this.$t("esgPlatform.textFieldNumeric") || "Numeric",
        "radio-button": this.$t("esgPlatform.radioButton") || "Radio Button",
        "check-boxes": this.$t("esgPlatform.checkBoxes") || "Check Boxes",
        "measurement-unit": this.$t("surveyBuilder.measurementUnit") || "Measurement Unit",
        "attach-file": this.$t("esgPlatform.attachFiles") || "Attach File",
        "digital-signature": this.$t("esgPlatform.digitalSignature") || "Digital Signature",
      };
      return map[type] || type;
    },
    formatQuestionType(type) {
      const map = {
        "text-field": this.$t("esgPlatform.textField") || "Text Field",
        "text-area": this.$t("esgPlatform.textArea") || "Text Area",
        numeric: this.$t("esgPlatform.textFieldNumeric") || "Text Field Numeric",
        "radio-button": this.$t("esgPlatform.radioButton") || "Radio Button",
        "check-boxes": this.$t("esgPlatform.checkBoxes") || "Check Boxes",
        date: this.$t("esgPlatform.date") || "Date",
        "digital-signature": this.$t("esgPlatform.digitalSignature") || "Digital Signature",
        "attach-files": this.$t("esgPlatform.attachFiles") || "Attach Files",
        "get-data": this.$t("esgPlatform.getData") || "Get Data",
        "dimitra-module": this.$t("surveyBuilder.dimitraModule") || "Dimitra Module",
      };
      return map[type] || type;
    },
    formatDate(d) {
      return d ? moment(d).format("DD/MM/YY") : "—";
    },
    stripHtml(html) {
      const tmp = document.createElement("div");
      tmp.innerHTML = html || "";
      return (tmp.textContent || tmp.innerText || "").trim();
    },
    notifyError(err) {
      this.$notify({
        text: (err && err.data && err.data.message) || this.$t("esgPlatform.errorLoadingSurveyResponses") || "Error loading responses",
        type: "error",
      });
    },
  },
};
</script>

<style scoped>
.sr-page {
  background: #f1f4f7;
  min-height: 100%;
}
.sr-content {
  padding: 24px 32px 32px;
}
/* header */
.sr-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}
.sr-header-left {
  flex: 1 1 480px;
  min-width: 0;
}
.sr-desc {
  overflow-wrap: anywhere;
}
.sr-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e1e1e;
  line-height: 1.2;
}
.sr-desc {
  font-size: 13px;
  color: #787878;
  margin-top: 4px;
}
.sr-count {
  font-size: 14px;
  color: #5c5959;
  margin-top: 10px;
}
.sr-count strong {
  color: #1e1e1e;
}
.sr-switcher {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
}
.sr-btn {
  border: 1px solid #d2d2d2;
  border-radius: 7px;
  padding: 10px 20px;
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.5px;
  color: #5c5959;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
}
.sr-btn:hover {
  border-color: #0ebf67;
  color: #117443;
}
.sr-btn.active {
  border: 0;
  color: #fff;
  background: #00bd73;
}
/* cards */
.sr-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.12);
  padding: 20px 24px;
}
/* back */
.sr-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #117443;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.3px;
  margin-bottom: 16px;
  cursor: pointer;
}
/* summary */
.sr-summary {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.12);
  padding: 20px 24px;
  margin-bottom: 20px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}
.sr-summary-label {
  font-size: 12px;
  color: #787878;
}
.sr-summary-value {
  font-size: 16px;
  font-weight: 600;
  color: #1e1e1e;
  margin-top: 4px;
}
/* sections + question cards */
.sr-section {
  margin-bottom: 8px;
}
.sr-section-title {
  font-size: 13px;
  font-weight: 600;
  color: #117443;
  margin: 8px 0 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.sr-qcard {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.12);
  padding: 16px 22px;
  margin-bottom: 16px;
}
.sr-qhead {
  display: flex;
  align-items: center;
  gap: 12px;
}
.sr-qnum {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #00bd73;
}
.sr-qtitle {
  font-size: 15px;
  font-weight: 600;
  color: #1e1e1e;
}
.sr-badge {
  display: inline-flex;
  align-items: center;
  border: 1px solid #d2d2d2;
  border-radius: 999px;
  padding: 3px 12px;
  font-size: 11px;
  font-weight: 500;
  color: #5c5959;
  background: #fff;
  white-space: nowrap;
}
.sr-chevron {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: transparent;
  border: 0;
}
.sr-chevron:hover {
  background: #f1f4f7;
}
.sr-qbody {
  padding-left: 38px;
  margin-top: 12px;
}
.sr-qdesc {
  font-size: 13px;
  color: #787878;
  margin-bottom: 12px;
}
.sr-qtitle-body {
  font-size: 15px;
  font-weight: 600;
  color: #1e1e1e;
  margin-bottom: 6px;
}
.sr-reject {
  margin-top: 8px;
  color: #f03737;
  font-size: 12px;
}
/* summary list */
.sr-summary-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.sr-summary-list .sr-qcard {
  box-shadow: none;
  border: 1px solid #e8e8e8;
  margin-bottom: 0;
}
.sr-summary-desc {
  padding-left: 38px;
  margin-top: 6px;
  margin-bottom: 0;
}
.sr-meta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.sr-meta-label {
  font-size: 12px;
  color: #787878;
  margin-right: 2px;
}
.sr-chip {
  display: inline-flex;
  align-items: center;
  border: 1px solid #e3e3e3;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  color: #444;
  background: #fff;
}
.sr-user-table {
  width: 100%;
  border-collapse: collapse;
}
.sr-user-table thead th {
  text-align: left;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  background: #00bd73;
}
.sr-user-table tbody td {
  padding: 10px 16px;
  font-size: 14px;
  color: #1e1e1e;
  border-bottom: 1px solid #f0f0f0;
}
.sr-user-count-col {
  text-align: center;
}
/* response tables */
.sr-resp-table {
  width: 100%;
  border-collapse: collapse;
}
.sr-resp-table thead th {
  text-align: left;
  border-bottom: 1px solid #e3e3e3;
  padding: 8px 12px 8px 0;
  font-size: 13px;
  color: #787878;
  font-weight: 500;
}
.sr-resp-table tbody td {
  padding: 10px 12px 10px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  color: #1e1e1e;
}
.sr-top {
  vertical-align: top;
}
/* by-question */
.sr-byq-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
.sr-dates {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
.sr-dates-label {
  font-size: 12px;
  color: #787878;
  margin-right: 2px;
}
.sr-date {
  border: 1px solid #e3e3e3;
  background: #fff;
  border-radius: 999px;
  padding: 5px 14px;
  font-size: 12px;
  color: #444;
  cursor: pointer;
  transition: all 0.15s ease;
}
.sr-date:hover {
  border-color: #0ebf67;
}
.sr-date.active {
  background: #e5f8f1;
  border-color: #0ebf67;
  color: #117443;
  font-weight: 600;
}
/* pagination */
.sr-pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}
/* option counts strip (responses-by-question, option types) */
.sr-optcount-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 4px;
}
.sr-optcount {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  background: #f1f4f7;
  border: 1px solid #e3e3e3;
}
.sr-optcount-label {
  font-size: 13px;
  color: #5c5959;
}
.sr-optcount-num {
  font-size: 13px;
  font-weight: 700;
  color: #117443;
}
/* misc */
.sr-muted {
  color: #979797;
}
.sr-icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 999px;
  border: 0;
  background: #fff;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.15);
  color: #117443;
  cursor: pointer;
}
.sr-icon-btn:hover {
  background: #e5f8f1;
}
.sr-progress-text {
  font-size: 14px;
  font-weight: 600;
  color: #1e1e1e;
}
</style>
